import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { animate, useMotionValue, type AnimationPlaybackControls, type MotionValue } from 'framer-motion'
import { GlobalEvents, GlobalState } from '@highlight/ipc'
import { useSidepanel } from '@renderer/overlay/atoms'
import { sidepanelSideSign, type SidepanelSide } from '@shared/sidepanel/side'
import { useViewActions, useViewNavigation } from '../view-atoms'
import { SIDEPANEL_SPRING, SIDEPANEL_SNAP_BACK_SPRING } from '../constants'
import { useSwipeDismiss } from '../swipe-gesture'
import { isHomeScrolling } from './sidepanel-scroll-state'
import { setOverlayInteractive } from '@highlight/ui'

interface UseSidepanelMotionOverrideOptions {
  /** Which screen edge the panel is anchored to. */
  side: SidepanelSide
  /** Panel width in px — the off-screen travel distance. */
  sidepanelWidth: number
}

interface SidepanelMotionOverride {
  /** The override motion value. Only applied to the panel's style while {@link isOverrideActive}. */
  x: MotionValue<number>
  /** True while a swipe gesture or the re-dock entrance is driving `x`. */
  isOverrideActive: boolean
  /** Animate out and reset — for closes that race an in-flight gesture (escape/hotkey). */
  resetForClose: () => void
  /** Deactivate the override — for exit-complete and exit-interrupt cleanup paths. */
  clearOverride: () => void
}

/**
 * Owns the temporary x-override on the sidepanel root: the two-finger
 * swipe-to-close gesture (Home only — an open View claims the swipe itself)
 * and the re-dock entrance that slides the panel in from the new edge when
 * its anchored side changes while open. Both drive the same motion value —
 * they can never be in flight simultaneously (a drag commit ends any swipe) —
 * so a single owner keeps the "who controls `x` right now" invariant local.
 */
export function useSidepanelMotionOverride({
  side,
  sidepanelWidth,
}: UseSidepanelMotionOverrideOptions): SidepanelMotionOverride {
  const [sidepanelState, setSidepanelState] = useSidepanel()
  const { forwardView } = useViewActions()
  const { canGoBack: isViewOpen, canGoForward } = useViewNavigation()

  const sideSign = sidepanelSideSign(side)
  const isOpen = sidepanelState === 'open'

  const x = useMotionValue(0)
  const [isSwiping, setIsSwiping] = useState(false)
  const [isRedocking, setIsRedocking] = useState(false)
  // Synchronous mirror of isRedocking, read by resetForClose when a close
  // races the entrance: mid-redock the entrance animation owns `x`, so the
  // value must be left in place for the exit spring.
  const isRedockingRef = useRef(false)

  /* ---------------------------- Swipe to close ---------------------------- */

  const { cancelGesture } = useSwipeDismiss({
    // The panel answers only while Home is on top — an open View owns its own
    // swipes (view-router.tsx) — and not during the quick tour or an active
    // scroll.
    claims: () => !isViewOpen && GlobalState.get('sidepanel.quickTourActive') !== true && !isHomeScrolling(),
    sideSign,
    onHorizontalStart: ({ edgeDeltaX }) => {
      // Disable body scrolling for the rest of the gesture
      setIsSwiping(true)
      // Only close blur when swiping toward the edge (dismiss direction)
      if (edgeDeltaX > 0) {
        GlobalEvents.emit('sidepanel:blur:close')
      }
    },
    onMove: (offset) => x.set(offset),
    onEnd: ({ shouldClose, shouldForward }) => {
      if (shouldForward && canGoForward) {
        setIsSwiping(false)
        x.jump(0)
        forwardView()
      } else if (shouldClose) {
        // Animate to closed position, then update state
        animate(x, sidepanelWidth * sideSign, {
          ...SIDEPANEL_SPRING,
          onComplete: () => {
            setIsSwiping(false)
            setOverlayInteractive(false)
            setSidepanelState('hidden')
          },
        })
      } else {
        // Snap back to open position
        animate(x, 0, {
          ...SIDEPANEL_SNAP_BACK_SPRING,
          onComplete: () => {
            setIsSwiping(false)
            GlobalEvents.emit('sidepanel:blur:restore')
          },
        })
      }
    },
  })

  // Reset the override whenever the panel (re)opens
  useEffect(() => {
    if (sidepanelState === 'open') {
      x.set(0)
    }
  }, [sidepanelState, x])

  // Sync isSwiping to GlobalState for SidepanelBody to disable scrolling during swipe
  useEffect(() => {
    GlobalState.set('sidepanel.isSwiping', isSwiping)
    return () => {
      GlobalState.set('sidepanel.isSwiping', false)
    }
  }, [isSwiping])

  /* --------------------------- Re-dock entrance --------------------------- */

  const prevSideRef = useRef(side)
  const redockAnimationRef = useRef<AnimationPlaybackControls | null>(null)

  const clearRedock = useCallback(() => {
    redockAnimationRef.current?.stop()
    redockAnimationRef.current = null
    isRedockingRef.current = false
    setIsRedocking(false)
  }, [])

  // When the anchored side changes while the panel is open, slide in from the
  // new edge instead of teleporting into place. Runs in a layout effect so the
  // off-screen start position is applied in the same frame as the anchor swap
  // (no flash at the new anchor).
  useLayoutEffect(() => {
    if (prevSideRef.current === side) {
      return
    }
    prevSideRef.current = side
    if (!isOpen) {
      return
    }
    x.jump(sidepanelWidth * sideSign)
    isRedockingRef.current = true
    setIsRedocking(true)
    redockAnimationRef.current = animate(x, 0, {
      ...SIDEPANEL_SPRING,
      onComplete: () => {
        clearRedock()
        x.jump(0)
      },
    })
  }, [side, sideSign, isOpen, sidepanelWidth, x, clearRedock])

  // Closing mid-entrance interrupts the spring, so onComplete never fires —
  // stop the entrance spring, but leave the override active. The exit spring
  // should keep using the current x value, and the parent's exit cleanup
  // (clearOverride) resets the redock state after exit completion.
  useLayoutEffect(() => {
    if (isOpen || !isRedockingRef.current) {
      return
    }
    redockAnimationRef.current?.stop()
    redockAnimationRef.current = null
  }, [isOpen])

  /* ------------------------------- Controls ------------------------------- */

  // Reset gesture state so a close's exit animation can run cleanly.
  const resetForClose = useCallback(() => {
    cancelGesture() // Ignore remaining events from any ongoing gesture
    if (isSwiping) {
      // Animate x to the closed position, then clean up. setIsSwiping(false)
      // happens before any jump back to 0 so the style override is removed
      // first, preventing a one-frame flash.
      animate(x, sidepanelWidth * sideSign, {
        ...SIDEPANEL_SPRING,
        onComplete: () => setIsSwiping(false),
      })
    } else {
      setIsSwiping(false)
      // Mid-redock the entrance animation owns x — leave it in place so the
      // exit spring slides out from the panel's current position instead of
      // snapping to fully open first.
      if (!isRedockingRef.current) {
        x.jump(0)
      }
    }
  }, [cancelGesture, isSwiping, x, sidepanelWidth, sideSign])

  // Deliberately leaves `x` where the exit left it. This runs from
  // AnimatePresence's onExitComplete, when the exiting element — a frozen
  // clone that still binds `x` in its style — is painted for one more frame
  // before React unmounts it; jumping to 0 here flashes the panel back to
  // fully open for that frame. The (re)open effect above resets `x` instead.
  const clearOverride = useCallback(() => {
    clearRedock()
    setIsSwiping(false)
  }, [clearRedock])

  return { x, isOverrideActive: isSwiping || isRedocking, resetForClose, clearOverride }
}
